import { Screen } from "../../../main/components/screen/Screen";
import { Box, Grid, Typography } from "@mui/material";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import DatePickerLocalizationAdapter from "../../../main/context/DatePickerLocalizationAdapter";
import { MainContext } from "../../../main/context/MainContextProvider";
import { useContext, useMemo, useState } from "react";
import dayjs from "dayjs";
import { usinasApi } from "../../../services/api";
import { useNavigate } from "react-router-dom";
import { DatePicker, DateValidationError } from "@mui/x-date-pickers";
import { formatDate, formatDateAndHour, isNumberNullOrNegative, numberPtBr, numberPtBrPercent01, numberPtBrPercent0100 } from "../../../main/utils/utils";
import { transformDateFormat } from "../../dashboard/helpers/format-number";
import CardBox from "../../../main/components/CardBox";
import { UsinaDetailsCard, UsinaDetailsInfo } from "./ReportDaily";

export default function ReportPersonalized() {
    const {
        selectedDashboardUsina: selectedUsina,
        toastNotifications,
        setToastNotifications,
        relatorioPersonalizadoLoading,
        relatorioPersonalizado,
        setRelatorioPersonalizadoLoading,
        setRelatorioPersonalizado,
        // theme,
    } = useContext(MainContext);

    const navigate = useNavigate()

    const [error, setError] = useState<DateValidationError | null>(null);

    const [reportStartDate, setReportStartDate] = useState<string>(
        new Date().toISOString().split("T")[0]
    );
    const [reportEndDate, setReportEndDate] = useState<string>(
        new Date().toISOString().split("T")[0]
    );

    const [reportErrors, setReportErrors] = useState<string[]>([]);

    const getRelatorioPersonalizado = async (reportStartDate: string, reportEndDate: string) => {
        setRelatorioPersonalizadoLoading(true);
        const newNotifications = {
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
                },
            ],
        };
        setToastNotifications(newNotifications);
        try {
            const reportPersonalizedResponse = await usinasApi.getPersonalizedReportUsina(
                reportStartDate,
                reportEndDate,
                selectedUsina?.usinaIds.id ?? ""
            );

            setRelatorioPersonalizado(reportPersonalizedResponse.data.relatorioPersonalizado);
            setReportErrors(reportPersonalizedResponse.data.errors);
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatório personalizado gerado com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/boletim-Personalizado")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para vizualizar.
                            </>
                        ),
                    },
                ],
            };
            setToastNotifications(newNotifications);
        } catch (error) {
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "error" as const,
                        text: "Erro ao gerar relatório personalizado, tente novamente!",
                    },
                ],
            };
            setToastNotifications(newNotifications);
        } finally {
            setRelatorioPersonalizadoLoading(false);
        }
    };

    const errorMessage = useMemo(() => {
        switch (error) {
            case "maxDate":
            case "minDate": {
                return `Por Favor selecione uma data depois de ${formatDate(
                    String(reportStartDate)
                )}`;
            }

            case "invalidDate": {
                return "Sua data é inválida";
            }

            default: {
                return "";
            }
        }
    }, [error, reportStartDate]);

    const prRealizado =
        isNumberNullOrNegative(
            relatorioPersonalizado?.prIrradiacaoPrevistoRealizado?.prRealizado
        ) ||
            (relatorioPersonalizado?.prIrradiacaoPrevistoRealizado?.prRealizado ?? 0) > 150
            ? "-"
            : `${numberPtBrPercent01(
                relatorioPersonalizado?.prIrradiacaoPrevistoRealizado?.prRealizado ?? 0, 2
            )}`;

    const irradiacaoMedia =
        isNumberNullOrNegative(
            relatorioPersonalizado?.prIrradiacaoPrevistoRealizado?.irradiacaoRealizada
        ) ||
            (relatorioPersonalizado?.prIrradiacaoPrevistoRealizado?.irradiacaoRealizada ?? 0) >
            200
            ? "-"
            : `${numberPtBrPercent0100(
                relatorioPersonalizado?.prIrradiacaoPrevistoRealizado?.irradiacaoRealizada ??
                0
            )}`;

    const potenciaMedia = isNumberNullOrNegative(
        relatorioPersonalizado?.potenciaAtivaMedia?.value
    )
        ? "-"
        : `${numberPtBr(relatorioPersonalizado?.potenciaAtivaMedia?.value ?? 0, 2)} MW`;

    const disponibilidadeOnOff = isNumberNullOrNegative(
        relatorioPersonalizado?.compiladoUsina?.disponibilidadeOnOff
    )
        ? "-"
        : `${numberPtBrPercent01(
            relatorioPersonalizado?.compiladoUsina?.disponibilidadeOnOff ?? 0,
            1,
            0
        )}`;

    const disponibilidadeOnOffEnabled =
        relatorioPersonalizado?.compiladoUsina?.disponibilidadeOnOffEnabled;

    const disponibilidadeMedia = isNumberNullOrNegative(
        relatorioPersonalizado?.disponibilidadePersonalizado?.disponibilidadeMedia
    )
        ? "-"
        : `${numberPtBrPercent0100(
            relatorioPersonalizado?.disponibilidadePersonalizado?.disponibilidadeMedia ?? 0,
            1,
            0
        )}`;

    const disponibilidadeTotal =
        isNumberNullOrNegative(relatorioPersonalizado?.disponibilidadeTotal?.value) ||
            (relatorioPersonalizado?.disponibilidadeTotal?.value ?? 0) > 100
            ? "-"
            : `${numberPtBrPercent01(
                relatorioPersonalizado?.disponibilidadeTotal?.value ?? 0,
                1,
                0
            )}`;

    return (
        <Screen.Root
            sx={{
                padding: "20px",
                height: "100vh",
                display: "flex",
                overflow: "auto",
                alignContent: "center",
            }}
        >
            <Screen.Container removePadding sx={{ height: "100%" }}>
                <Grid
                    container
                    sx={{
                        height: "100%",
                        paddingBottom: "16px",
                    }}
                    spacing={2}
                >
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "20px",
                            alignItems: "center",
                            justifyContent: relatorioPersonalizado ? undefined : "center",
                        }}
                    >
                        <Box
                            sx={{
                                maxWidth: "400px",
                                minWidth: { xs: "250px", md: "400px" },
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                Relatório Personalizado
                            </Typography>
                            <Box sx={{ width: "100%", marginY: "16px" }}>
                                <DatePickerLocalizationAdapter>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        slotProps={{ textField: { size: "small" } }}
                                        maxDate={dayjs(new Date())}
                                        sx={{ width: "49%", marginRight: "8px" }}
                                        label="Data Inicial"
                                        value={dayjs(reportStartDate)}
                                        onChange={(newValue) => setReportStartDate(newValue?.format("YYYY-MM-DD") ?? "")}
                                    />
                                </DatePickerLocalizationAdapter>
                                <DatePickerLocalizationAdapter>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        minDate={dayjs(reportStartDate)}
                                        maxDate={dayjs(new Date())}
                                        slotProps={{
                                            textField: { size: "small", helperText: errorMessage },
                                        }}
                                        sx={{ width: "49%" }}
                                        label="Data Final"
                                        onError={(newError) => setError(newError)}
                                        value={dayjs(reportEndDate)}
                                        onChange={(newValue) => setReportEndDate(newValue?.format("YYYY-MM-DD") ?? "")}
                                    />
                                </DatePickerLocalizationAdapter>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", md: "row" },
                                    width: "100%",
                                    justifyContent: "space-between",
                                }}
                            >
                                <PrimaryBaseButton
                                    disabled={
                                        // !currentUsina?.reportOptions?.PersonalizedReportEnabled ||
                                        !reportStartDate ||
                                        relatorioPersonalizadoLoading
                                    }
                                    sx={{ width: "100%" }}
                                    loading={relatorioPersonalizadoLoading}
                                    title="Gerar Relatório"
                                    onClick={() => getRelatorioPersonalizado(reportStartDate, reportEndDate)}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    {relatorioPersonalizado && (
                        <>
                            <Grid sx={{ width: "100%" }} item xs={12}>
                                <Typography variant="h6" sx={{ paddingBottom: "20px" }}>
                                    {`${selectedUsina?.name} - ${transformDateFormat(
                                        relatorioPersonalizado.dataDeReferenciaInicial
                                    )} - ${transformDateFormat(
                                        relatorioPersonalizado.dataDeReferenciaFinal
                                    )}`}
                                </Typography>
                            </Grid>
                            {reportErrors.map((error, i) => (
                                <Grid key={i} sx={{ width: "100%" }} item xs={12}>
                                    <Typography
                                        sx={{
                                            paddingBottom: "10px",
                                            color: "#ff6060",
                                            fontSize: 14,
                                        }}
                                    >
                                        {error}
                                    </Typography>
                                </Grid>
                            ))}
                            <Grid sx={{ width: "100%" }} item xs={12}>
                                <CardBox
                                    dateLeft={`Dados do relatório coletados em: ${formatDateAndHour(
                                        relatorioPersonalizado.dataDeGeracao
                                    )}`}
                                    leftTopCalc={290}
                                    widthTopCalc="305"
                                    sx={{
                                        height: "100%",
                                        width: "100%",
                                        paddingY: "32px",
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "16px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexDirection: "column",
                                            display: "flex",
                                            justifyContent: "space-around",
                                            width: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flexDirection: { xs: "column", md: "row" },
                                                display: "flex",
                                                justifyContent: "space-around",
                                            }}
                                        >
                                            <UsinaDetailsInfo
                                                title="Código da Usina: "
                                                value={relatorioPersonalizado.dadosUsina?.cod ?? ""}
                                            />
                                            <UsinaDetailsInfo
                                                title="Número de Inversores: "
                                                value={
                                                    relatorioPersonalizado.dadosUsina?.numeroInversores ?? 0
                                                }
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                flexDirection: { xs: "column", md: "row" },
                                                display: "flex",
                                                justifyContent: "space-around",
                                            }}
                                        >
                                            <UsinaDetailsInfo
                                                title="Potência Nominal: "
                                                value={`${numberPtBr(
                                                    relatorioPersonalizado.dadosUsina?.potencia ?? 0 / 1000,
                                                    1
                                                )} MW`}
                                            />
                                            <UsinaDetailsInfo
                                                title="Número de Módulos: "
                                                value={relatorioPersonalizado.dadosUsina?.numeroModulos ?? 0}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            flexDirection: { xs: "column", md: "row" },
                                            display: "flex",
                                            width: "100%",
                                            justifyContent: "space-around",
                                        }}
                                    >
                                        <UsinaDetailsCard
                                            title="PR Realizado"
                                            value={prRealizado}
                                        />
                                        <UsinaDetailsCard
                                            title="Irradiação Realizada"
                                            value={irradiacaoMedia}
                                        />
                                        <UsinaDetailsCard
                                            title="Potência Média Dia"
                                            value={potenciaMedia}
                                        />
                                        {!disponibilidadeOnOffEnabled && (
                                            <UsinaDetailsCard
                                                title="Disponibilidade Média"
                                                value={disponibilidadeMedia}
                                            />
                                        )}
                                    </Box>
                                    {disponibilidadeOnOffEnabled && (
                                        <Box
                                            sx={{
                                                flexDirection: { xs: "column", md: "row" },
                                                display: "flex",
                                                width: "100%",
                                                justifyContent: "space-around",
                                            }}
                                        >
                                            <UsinaDetailsCard
                                                title="Disponibilidade Média"
                                                value={disponibilidadeMedia}
                                            />
                                            <UsinaDetailsCard
                                                title="Disponibilidade On-Off"
                                                value={disponibilidadeOnOff}
                                            />
                                            <UsinaDetailsCard
                                                title="Disponibilidade Total"
                                                value={disponibilidadeTotal}
                                            />
                                        </Box>
                                    )}
                                </CardBox>
                            </Grid>
                        </>)}
                </Grid>
            </Screen.Container>
        </Screen.Root>
    )
}